/**
 * Endpoints du domaine KYC & Identités (kyc-service) — source canonique.
 *
 * Chemins copiés TELS QUELS de la collection Postman / du guide §6.2
 * (`docs/guides/`) : préfixe gateway `/gw/kyc`, listes en `POST .../all`
 * (§4.2). Ne jamais inventer un chemin.
 *
 * `@/core/api/endpoints` ré-exporte ces valeurs (pas de duplication) : toute
 * nouvelle route kyc s'ajoute ICI, jamais dans core.
 *
 * Rôles Keycloak exigés (guide §6.2, vue d'ensemble) rappelés en commentaire
 * — la garde front par rôle arrive avec la phase 1.2.
 */
export const kycEndpoints = {
  parties: {
    /** POST — authentifié. Idempotency-Key OBLIGATOIRE (§4.7). */
    add: "/gw/kyc/api/v1/Parties/add",
    /** POST — flashpay-superadmin, flashpay-admin, flashpay-agents. */
    all: "/gw/kyc/api/v1/Parties/all",
    /** GET — flashpay-superadmin, flashpay-admin, flashpay-agents. */
    detail: (partyId: string) => `/gw/kyc/api/v1/Parties/${partyId}`,
    /** PUT — flashpay-superadmin, flashpay-admin, flashpay-agents. */
    update: (partyId: string) => `/gw/kyc/api/v1/Parties/${partyId}`,
    /** POST — flashpay-superadmin, flashpay-admin, flashpay-agents. Un seul critère (téléphone, email OU n° de pièce). */
    lookup: "/gw/kyc/api/v1/Parties/lookup",
    /** POST — flashpay-superadmin, flashpay-admin. HIT/UNDER_REVIEW → CLEAR exige approvalRequestId (KYC_FORCE_OVERRIDE). */
    sanctionsScreening: (partyId: string) =>
      `/gw/kyc/api/v1/Parties/${partyId}/sanctions-screening`,
    /**
     * POST — guide v2.3 §6.2. Résout un lot d'IDs → noms d'affichage
     * UNIQUEMENT (pas de téléphone / email / pièce). ≤ 200 par appel.
     * Destiné aux écrans qui n'affichent que des IDs techniques.
     */
    names: "/gw/kyc/api/v1/Parties/names",
  },
  /**
   * POST — guide v3.1. Résout un lot d'identités (≤ 200) en numéros de
   * téléphone E.164 déchiffrés. Appelé par wallet-service pour afficher
   * expéditeur/destinataire dans l'historique des opérations.
   * Corps `{ partyIds: string[] }` ; les identités inconnues ou sans
   * téléphone sont absentes de la réponse (`{ partyId, phone }`).
   * Déclaré pour parité documentaire : aucun hook, aucun écran.
   */
  /** INTERNAL — flashpay-service only */
  partiesPhones: "/gw/kyc/api/v1/Parties/phones",
  kycCases: {
    /** POST — authentifié. Idempotency-Key OBLIGATOIRE (§4.7). */
    add: "/gw/kyc/api/v1/KycCases/add",
    /** POST — flashpay-superadmin, flashpay-admin, flashpay-agents. */
    all: "/gw/kyc/api/v1/KycCases/all",
    /** GET — authentifié. */
    detail: (kycCaseId: string) => `/gw/kyc/api/v1/KycCases/${kycCaseId}`,
    /** POST — authentifié. DRAFT → SUBMITTED, exige ≥ 1 pièce confirmée. */
    submit: (kycCaseId: string) => `/gw/kyc/api/v1/KycCases/${kycCaseId}/submit`,
    /** POST — flashpay-superadmin, flashpay-admin. Séparation des tâches : l'agent collecteur ne peut pas valider. */
    review: (kycCaseId: string) => `/gw/kyc/api/v1/KycCases/${kycCaseId}/review`,
  },
  kycDocuments: {
    /** POST — authentifié. URL présignée PUT vers le stockage (le binaire ne traverse jamais l'API). */
    presign: (kycCaseId: string) =>
      `/gw/kyc/api/v1/kyc-cases/${kycCaseId}/documents/presign`,
    /** POST — authentifié. Dossier encore en DRAFT. */
    confirm: (kycCaseId: string) =>
      `/gw/kyc/api/v1/kyc-cases/${kycCaseId}/documents/confirm`,
    /** GET — flashpay-superadmin, flashpay-admin (rôles de revue uniquement). */
    downloadUrl: (kycCaseId: string, documentId: string) =>
      `/gw/kyc/api/v1/kyc-cases/${kycCaseId}/documents/${documentId}/download-url`,
    /** POST — flashpay-superadmin, flashpay-admin. decision=VALIDATED|REJECTED en query. */
    review: (kycCaseId: string, documentId: string) =>
      `/gw/kyc/api/v1/kyc-cases/${kycCaseId}/documents/${documentId}/review`,
  },
  companyProfiles: {
    /** POST — authentifié. */
    add: "/gw/kyc/api/v1/CompanyProfiles/add",
    /** GET — flashpay-superadmin, flashpay-admin. */
    byParty: (partyId: string) =>
      `/gw/kyc/api/v1/CompanyProfiles/party/${partyId}`,
  },
  agents: {
    /** POST — flashpay-superadmin, flashpay-admin. */
    add: "/gw/kyc/api/v1/Agents/add",
    /** POST — flashpay-superadmin, flashpay-admin. */
    all: "/gw/kyc/api/v1/Agents/all",
    /** GET — flashpay-superadmin, flashpay-admin. */
    detail: (agentId: string) => `/gw/kyc/api/v1/Agents/${agentId}`,
    /** POST — flashpay-superadmin, flashpay-admin. status en query param. */
    setStatus: (agentId: string) => `/gw/kyc/api/v1/Agents/${agentId}/status`,
    /**
     * GET — guide v2.4 §6.2. Lookup agent par sub Keycloak.
     * Rôles : flashpay-agents, flashpay-service. Utilisé par les parcours
     * Non-Member (récupère `agentId` + `agentPartyId` de la session agent).
     */
    byKeycloak: (keycloakUserId: string) =>
      `/gw/kyc/api/v1/Agents/by-keycloak/${keycloakUserId}`,
  },
  agentEnrollmentSessions: {
    /** POST — flashpay-agents, flashpay-superadmin, flashpay-admin. */
    add: "/gw/kyc/api/v1/AgentEnrollmentSessions/add",
    /** POST — flashpay-agents, flashpay-superadmin, flashpay-admin. */
    complete: (agentEnrollmentSessionId: string) =>
      `/gw/kyc/api/v1/AgentEnrollmentSessions/${agentEnrollmentSessionId}/complete`,
  },
} as const;
